export enum ItemTypes {
  SOCIAL_MEDIA_PROFILE = "social_media_profile",
  SOCIAL_MEDIA_POST = "social_media_post",
  SOCIAL_MEDIA_POST_WITH_CONSENSUS = "social_media_post_with_consensus",
  WEB_CONTENT_PROFILE = "web_content_parent",
  WEB_CONTENT = "web_content",
  AFFILIATION = "affiliation",
}

export enum ThreeSixtyItemTypes {
  DILIGENCE = "diligence",
  NEWS = "news",
  LEGAL = "legal_cases",
  SUMMARY = "summaries",
  FAMA_REPORT = "fama_reports",
}

export enum MediaTypes {
  Text = "text",
  Image = "image",
  Video = "video",
}

export enum PostTypes {
  Original = "original",
  Liked = "liked",
  Shared = "shared",
}

export enum RatingTypes {
  Manual = "manual",
  System = "system",
}
export enum ItemsForReviewSortCriteria {
  Date = "date",
  Behavior = "behavior",
}

export enum ArticleActions {
  REVIEW = "REVIEW",
  ADDED = "ADDED",
  REMOVED = "REMOVED",
}

export enum ProfileAddedBy {
  CLIENT = "CLIENT",
  INVESTIGATOR = "INVESTIGATOR",
  VETRIC = "VETRIC",
  WEB_CONTENT_SCRAPER = "WEB_CONTENT_SCRAPER",
}

export interface ItemsForReviewRequest {
  report_uuid?: string;
  item_type_name: (ItemTypes | string)[];
  rating_type?: RatingTypes;
  page_num?: number;
  page?: number;
  page_size?: number;
  item_source_name?: string[];
  item_post_type?: string[]; // examples: liked, shared, original
  min_date?: string;
  max_date?: string;
  sort_criteria?: ItemsForReviewSortCriteria | string;
  behaviors?: string[];
  keywords?: string[];
  topics?: string[];
}

export interface ArticlesForReviewRequest {
  report_id?: string;
  page_num?: number;
  page_size?: number;
  article_action: ArticleActions;
  include_all?: boolean;
}

export interface ItemsForSummaryRequest {
  report_id: string;
  item_types?: (ItemTypes | string)[];
  item_subtypes?: string[];
  sources?: string[];
  min_date?: string;
  max_date?: string;
  behaviors?: string[];
  keywords?: string[];
  topics?: string[];
  sort?: ItemsForReviewSortCriteria | string;
  page_size?: number;
  page_number: number;
}

export interface ItemsForReviewResponse {
  items: ItemWithMetaData[];
  total: number;
  page: number;
  size: number;
}

export interface TranslationResponse {
  original: string;
  language: string;
  translated: string;
  external_id: string;
}

export interface Translation {
  original: {
    text: string;
    title: string;
  };
  language: string;
  translated: {
    text: string;
    title: string;
  };
  external_id: string;
}

export interface ItemTranslation {
  language: string;
}

export interface ItemWithMetaData {
  item: Item;
  score: Score[];
  rating: Rating[];
  entities: RelevantEntity[];
  source?: Source;
  type: Type;
  behavior?: string;
  keywords?: Keyword[];
  topics?: TopicLabel[];
  translation?: ItemTranslation;
}

export interface TopicLabel {
  lens_topics_uuid: string;
  topic_group_name: string;
  label: string;
}

export interface AffiliationItemWithMetaData {
  item: Item;
  affiliation_activity: string;
  affiliation_source: string;
  affiliation_type: string;
  created_at: string;
  media_urls: string[];
  text: string;
  url: string;
  rating: Rating[];
  keywords?: Keyword[];
}

export interface ItemGroupCount {
  name: string;
  count: number;
}

export interface GroupCount {
  groups: ItemGroupCount[];
  total: number;
}

export interface TopicLabelCount {
  label: string;
  topic_group_name: string;
  count: number;
}

export interface TopicGroupCount {
  lens_topics_uuid: string;
  name: string;
  count: number;
  labels?: TopicLabelCount[];
}

export interface TopicCount {
  groups?: TopicGroupCount[];
  total: number;
}

export interface FlagCountResponse {
  behaviors: GroupCount;
  item_sources: GroupCount;
  item_types: GroupCount;
  post_types: GroupCount;
}

export interface FlagCountRequest {
  report_uuid: string;
  item_type_name?: string[];
  item_source_name?: string[]; // examples: liked, shared, original
  item_post_type?: string[];
  min_date?: string;
  max_date?: string;
  behaviors?: string[];
  keywords?: string[];
  topics?: string[];
  rating_type?: RatingTypes;
}

export interface RemovePostItemRequest {
  item_uuid: string;
  modified_by: string; // TODO: remove once the actor uuid is resolved
}

export interface RestorePostItemRequest {
  item_uuid: string;
  modified_by: string; // TODO: remove once the actor uuid is resolved
}

export interface ItemPostRating {
  name: string;
  score_type: string;
}

export interface MediaRequest {
  external_url: string;
  internal_url?: string;
  thumbnail_url?: string;
  type: MediaTypes;
}

export interface CreatePostApi {
  report_uuid: string;
  profile_uuid: string;
  profile_url: string;
  url: string;
  type?: PostTypes;
  text: string;
  site_id: string;
  created_at: string;
  ratings: ItemPostRating[];
  media: MediaRequest[];
}

export interface CreateArticleApi {
  report_uuid: string;
  profile_uuid: string;
  url: string;
  title: string;
  text: string;
  score: number;
  action: "review" | "skip";
  created_at?: string;
  status: "TO_DO" | "SKIP";
  media_url?: string;
  ratings: ItemPostRating[];
}

export interface CreateAffiliationApi {
  affiliation_type: string;
  url: string;
  affiliation_source: string;
  affiliation_activity: string;
  text: string;
  created_at?: string;
  media_urls: string[];
  report_uuid: string;
  ratings: ItemPostRating[];
}

export interface UpdateAffiliationApi {
  item_uuid: string;
  content: CreateAffiliationApi;
  ratings: ItemPostRating[];
  keywords?: Keyword[];
}

export interface CreateArticleRatingRequest {
  item_uuid: string;
  article_action: ArticleActions;
}

export interface UpdateItemPostRatingRequest {
  item_uuid: string;
  ratings: ItemPostRating[];
}

export interface UpdateItemPostRatingResponse {
  item_uuid: string;
  ratings: Rating[];
}

export interface Item {
  external_id: string;
  in_report: boolean;
  manually_added: boolean;
  content: any;
  is_reviewed: boolean;
}

export interface Score {
  external_id: string;
  score: number;
  name: string;
  score_type: string;
  action: string;
}

export interface Rating {
  external_id: string;
  rater_uuid: string;
  name: string;
  score_type: string;
}

export interface RelevantEntity {
  external_id: string;
  value_type: string;
  value: string;
  field: string;
}

export interface Keyword {
  external_id: string;
  keyword_group_uuid: string;
  matches: KeywordMatches;
}
export interface KeywordMatches {
  exclude_exact: string[];
  exclude_fuzzy: string[];
  include_exact: string[];
  include_fuzzy: string[];
}

export interface KeywordOptions {
  keyword_options: Set<string>;
}
export interface ConfirmContent {
  profile_confirm_entities: string[];
  profile_confirm_other_text?: string;
  [key: string]: any;
}

export interface ProfileConfirmItemRequest {
  item_uuid: string;
  content: ConfirmContent;
}

export interface CreateItemPostRatingRequest {
  item_uuid: string;
  ratings: ItemPostRating[];
}

export interface CreateItemPostRatingResponse {
  item_uuid: string;
  ratings: ItemPostRating[];
}

export interface KeywordsCountRequest {
  report_uuid: string;
  item_type_name?: string[];
  item_source_name?: string[]; // examples: liked, shared, original
  item_post_type?: string[];
  min_date?: string;
  max_date?: string;
  behaviors?: string[];
  keywords?: string[];
  topics?: string[];
}

export type TopicCountRequest = KeywordsCountRequest;

//
export interface Type {
  external_id: string;
  name: string;
}
export interface Source {
  external_id: string;
  name: string;
}

export interface CreateManualScrapePostResponse {
  external_id: string;
  is_deleted: boolean;
  in_report: boolean;
  manually_added: boolean;
  content: {
    created_at: string;
    profile_url: string;
    url: string;
    type: string;
    text: string;
    media: {
      external_url: string;
      internal_url: string;
      thumbnail_url: string;
      type: string;
      caption: string;
    }[];
    site_id: string;
  };
  is_reviewed: boolean;
  scrape_id: any;
}

export interface UpdateWebContentApi {
  item_uuid: string;
  content: { url: string; text: string; score: string; title: string };
  ratings: ItemPostRating[];
}

export interface ItemSource {
  external_id?: string;
  name: string;
  url_regex?: string;
  scraper_config?: { [key: string]: string | boolean | number };
  display?: string;
  icon?: string;
}
